import { Stripe, StripeElements } from "@stripe/stripe-js";
import { BillingData, PaymentMethod } from '../BillingTypes';
import { saveData } from "services/api/saveData";

interface BillingDetailsRef {
    current: {
        getCardElement?: () => any;
        getIbanElement?: () => any;
    } | null;
}

export const createSepaSetupIntent = async (
    stripe: Stripe | null,
    elements: StripeElements | null,
    stripeCustomerId: string,
    environmentHash: string,
    billingData: BillingData,
    paymentMethodData: PaymentMethod,
    billingDetailsRef: BillingDetailsRef
) => {
    // Return if stripe is not initialized
    if (!stripe || !elements) {
        console.error("Stripe not initialized");
        return null;
    }

    // Get the client secret from the backend
    const response = await saveData({
        apiUrl: `create_setup_intent/${environmentHash}`,
        method: "post",
        data: { 
            stripe_customer_id: stripeCustomerId
        }
    });

    // Return error if creation failed
    if (response?.status !== 200) {
        console.error("Failed to create setup intent");
        return null;
    }

    // Get the client secret from the response
    const clientSecret = response.data.client_secret;

    // Get the data from the iban element
    const ibanElement = billingDetailsRef.current?.getIbanElement?.();
    if (!ibanElement) {
        console.error("IBAN element not found");
        return null;
    }

    // Check if the company name and email are given
    const { company_name, email } = billingData;
    if (!company_name || !email) {
        console.error("No company name or email is set.");
        return null;
    }

    // Confirm the setup intent and create the mandate
    const { setupIntent, error } = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
            sepa_debit: ibanElement,
            billing_details: {
                name: company_name,
                email: email,
            },
        }}
    );

    // Return error if confirmation failed
    if (error || !setupIntent) {
        console.error("Error confirming SEPA setup:", error);
        return null;
    }

    // Return error if setup intent not succeeded
    if (setupIntent.status !== "succeeded") {
        console.error("SetupIntent not succeeded:", setupIntent.status);
        return null;
    }

    // Save the payment method
    try {
        const saveResponse = await saveData({
            apiUrl: `post_payment_method`,
            method: 'post',
            data: {
                stripe_customer_id: stripeCustomerId,
                setup_intent_id: setupIntent.id,
                sepa_account_holder: paymentMethodData.sepa_account_holder,
            }
        });

        if (saveResponse?.status !== 201) {
            throw new Error("Failed to save payment method");
        }

        return saveResponse.data;
    } catch (error) {
        console.error("Error saving payment method:", error);
        return null;
    }
};